import { Link } from "react-router-dom";
import {
  Anchor,
  Box,
  Group,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconAlertOctagon,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
  IconPoint,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { InboxSection } from "../../api/console";
import { useI18n } from "../../i18n/context";

dayjs.extend(relativeTime);

const SEV_TOKEN = { critical: "error", high: "warning", medium: "info", low: "state-offline" } as const;

const SEVERITY = {
  critical: { color: "red", icon: IconAlertOctagon },
  high: { color: "orange", icon: IconAlertTriangle },
  medium: { color: "yellow", icon: IconInfoCircle },
  low: { color: "gray", icon: IconPoint },
} as const;

/** Prioritised action queues. Empty sections are hidden, not shown as zero. */
export function InboxList({
  sections,
  compact = false,
}: {
  sections: InboxSection[];
  compact?: boolean;
}) {
  const { t, tx } = useI18n();
  const open = sections.filter((s) => s.count > 0);

  if (open.length === 0) {
    return (
      <Group gap="sm" py="md" justify="center">
        <ThemeIcon color="teal" variant="light" radius="xl" aria-hidden>
          <IconCircleCheck size={18} />
        </ThemeIcon>
        <Text c="dimmed">{t("inbox.allClear")}</Text>
      </Group>
    );
  }

  return (
    <Stack gap={0}>
      {open.map((s, idx) => {
        const sev = SEVERITY[s.severity];
        const items = compact ? s.items.slice(0, 3) : s.items;
        return (
          <Box
            key={s.key}
            py="sm"
            style={idx > 0 ? { borderTop: "1px solid var(--border-subtle)" } : undefined}
          >
            <Group justify="space-between" wrap="nowrap" mb={items.length ? 4 : 0}>
              <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
                <sev.icon
                  size={16}
                  stroke={1.75}
                  aria-hidden
                  style={{ flexShrink: 0, color: `var(--${SEV_TOKEN[s.severity]}-fg)` }}
                />
                <Text fw={600} size="sm" truncate>
                  {tx(`inbox.section.${s.key}`, s.title)}
                </Text>
                <Text size="sm" c="dimmed" className="num" style={{ flexShrink: 0 }}>
                  {s.count}
                </Text>
              </Group>
              <Anchor component={Link} to={s.link} size="xs" style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                {t("inbox.viewAll")}
              </Anchor>
            </Group>
            <Stack gap={0} pl={24}>
              {items.map((it) => (
                <UnstyledButton
                  key={it.id}
                  component={Link}
                  to={it.link}
                  px={8}
                  py={6}
                  ml={-8}
                  style={{ borderRadius: 6 }}
                  className="inbox-row"
                >
                  <Group justify="space-between" wrap="nowrap" gap="sm">
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" truncate>
                        {it.title}
                      </Text>
                      {it.detail && (
                        <Text size="xs" c="dimmed" truncate>
                          {it.detail}
                        </Text>
                      )}
                    </div>
                    {it.at && (
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {dayjs(it.at).fromNow()}
                      </Text>
                    )}
                  </Group>
                </UnstyledButton>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
